/**
 * Where the sign-in lives (2026-10-17, "Remember me").
 *
 * Remembered: localStorage, kept when the app or browser closes; the server
 * gives such a session 30 days and it is renewed while the app is open.
 * Not remembered: sessionStorage, gone when the window closes, so the next
 * launch asks again. Only one of the two ever holds a token.
 *
 * Every access is wrapped: storage can be blocked (private windows, site data
 * off), and a sign-in that cannot be kept should still work for the page.
 */
const TOKEN = "flyvac_token";
const USER = "flyvac_user";

const areas = () => {
  const out = [];
  try { if (globalThis.localStorage) out.push(globalThis.localStorage); } catch { /* blocked */ }
  try { if (globalThis.sessionStorage) out.push(globalThis.sessionStorage); } catch { /* blocked */ }
  return out;
};
const read = (a, k) => { try { return a.getItem(k); } catch { return null; } };
const write = (a, k, v) => { try { a.setItem(k, v); } catch { /* blocked */ } };
const drop = (a, k) => { try { a.removeItem(k); } catch { /* blocked */ } };

/** The area holding the current token, or null. */
const holder = () => areas().find((a) => read(a, TOKEN)) || null;

export function getToken() {
  const a = holder();
  return a ? read(a, TOKEN) : null;
}

export function getUser() {
  const a = holder();
  const raw = a ? read(a, USER) : null;
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

/** Is the current session a remembered one? */
export function isRemembered() {
  const a = holder();
  try { return !!a && a === globalThis.localStorage; } catch { return false; }
}

/** A new sign-in. Clears the other area so there is only ever one token. */
export function saveSession(token, user, remember) {
  clearSession();
  let target = null;
  try { target = remember ? globalThis.localStorage : globalThis.sessionStorage; } catch { /* blocked */ }
  if (!target) return;
  write(target, TOKEN, token);
  write(target, USER, JSON.stringify(user));
}

/** A renewed token or fresher user, kept wherever the session already is. */
export function updateSession({ token, user } = {}) {
  const a = holder();
  if (!a) return;
  if (token) write(a, TOKEN, token);
  if (user) write(a, USER, JSON.stringify(user));
}

export function clearSession() {
  for (const a of areas()) { drop(a, TOKEN); drop(a, USER); }
}

/** exp and iat from a JWT, unverified: only for scheduling renewal. */
export function tokenTimes(token) {
  try {
    const part = String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(part + "=".repeat((4 - (part.length % 4)) % 4)));
    return { exp: Number(json.exp) || 0, iat: Number(json.iat) || 0 };
  } catch {
    return { exp: 0, iat: 0 };
  }
}

/** Renew once half the session has gone, or with under an hour left. A
 *  token we cannot read is left alone: the server will say if it is bad. */
export function needsRenewal(token, nowSec = Date.now() / 1000) {
  const { exp, iat } = tokenTimes(token);
  if (!exp) return false;
  const left = exp - nowSec;
  if (left <= 0) return false;             // too late: /auth/refresh needs a valid token
  const life = iat && exp > iat ? exp - iat : 0;
  return left < 3600 || (life > 0 && left < life / 2);
}
